import { VariableKeyBox } from "@/workflow/components/VariableKeyBox";
import { useGetServiceEndpoints } from "@/queries/interoperability/interoperability.api";
import type { ParameterDefinition } from "../types";
import { useMemo } from "react";
import { Icon } from "@elaniintc/goes-design-system";

interface InteroperabilityOutputBlockProps {
  serviceId?: string;
  endpointId?: string;
}

export const InteroperabilityOutputBlock = ({
  serviceId,
  endpointId,
}: InteroperabilityOutputBlockProps) => {
  // Get the endpoints for the selected service
  const { data: endpoints, isLoading } = useGetServiceEndpoints(
    serviceId || "",
  );

  // Find the selected endpoint data
  const selectedEndpointData = useMemo(() => {
    if (!endpoints || !endpointId) return null;
    return endpoints.find(
      (e: { id: string; service: unknown }) => e.id === endpointId,
    );
  }, [endpoints, endpointId]);

  // Extract output variables from response 200 data
  const outputVariables = useMemo(() => {
    if (!selectedEndpointData?.service) return [];

    const service = selectedEndpointData.service as {
      responses?: {
        200?: {
          data?: Record<string, ParameterDefinition>;
        };
      };
    };

    const responseData = service.responses?.["200"]?.data;
    if (!responseData) return [];

    return Object.entries(responseData).map(([key, value]) => {
      // Normalize type to match VariableKeyBox expectations
      let normalizedType = value.type || "string";
      // Map common type variations to standard types
      if (normalizedType === "text") {
        normalizedType = "string";
      }
      return {
        name: value.display_name || key,
        type: normalizedType,
        description: value.description || "",
      };
    });
  }, [selectedEndpointData]);

  if (isLoading) {
    return (
      <Icon
        name="spinner-third"
        className="animate-spin text-sm text-on-disabled"
        type="far"
      />
    );
  }

  if (!serviceId || !endpointId || outputVariables.length === 0) {
    return <VariableKeyBox name="response" type="text" />;
  }

  return (
    <>
      {outputVariables.map((variable, index) => (
        <VariableKeyBox
          key={`variable-${variable.name}-${index}`}
          name={variable.name}
          type={variable.type}
        />
      ))}
    </>
  );
};
